import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  StarIcon,
  EyeSlashIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import { listTariffs, updateTariff, deleteTariff } from "../api/tariffsAPI";
import TariffEditor from "../components/tariffs/TariffEditor";
import { ConfirmDialog } from "../components/ChatList";

const AUDIENCES = [
  { value: "business", label: "Бизнес" },
  { value: "individual", label: "Физ. лица" },
];

const ICON_GLYPH = { star: "☆", star_filled: "★", medal_star: "🏅" };
const SUPPORT = { standard: "обычная", fast: "быстрая", instant: "мгновенная" };

const money = (v, c = "₸") => `${Number(v || 0).toLocaleString("ru-RU")} ${c || "₸"}`;
const days = (n) => (n % 365 === 0 ? `${n / 365} г.` : n % 30 === 0 ? `${n / 30} мес.` : `${n} дн.`);

const saleState = (t) => {
  if (!t.is_active) return { label: "Скрыт", cls: "bg-gray-500/15 text-text-muted border-gray-500/30", icon: EyeSlashIcon };
  if (t.available_until && new Date(t.available_until) < new Date(new Date().toDateString()))
    return { label: "Продажа завершена", cls: "bg-rose-500/15 text-rose-500 border-rose-500/30", icon: EyeSlashIcon };
  return { label: "В продаже", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", icon: CheckIcon };
};

const Perk = ({ label, value, off }) => (
  <li className={`flex items-center justify-between gap-3 py-1 text-xs ${off ? "text-text-muted" : "text-text-secondary"}`}>
    <span>{label}</span>
    <span className={`font-medium tabular-nums ${off ? "" : "text-text-primary"}`}>{value}</span>
  </li>
);

const TariffCard = ({ t, onEdit, onToggle, onDelete, busy }) => {
  const c = t.conditions || {};
  const state = saleState(t);
  const maxAds = !c.max_active_announcements ? "∞" : c.max_active_announcements;
  return (
    <article className={`flex flex-col rounded-block border bg-bg-card shadow-sm transition-opacity ${t.is_popular ? "border-[#4F45E5]/50" : "border-border-primary"} ${!t.is_active ? "opacity-70" : ""} ${busy ? "pointer-events-none opacity-50" : ""}`}>
      <div className="flex items-start gap-3 border-b border-border-primary p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-grad text-lg text-white">{ICON_GLYPH[t.icon] || "☆"}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-base font-semibold text-text-primary">{t.name}</h3>
            {t.is_popular && <StarIcon className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" title="Популярный" />}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-px text-[11px] font-medium ${state.cls}`}>
              <state.icon className="h-3 w-3" />
              {state.label}
            </span>
            {t.is_archived && (
              <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-px text-[11px] font-medium text-amber-500">
                <ArchiveBoxIcon className="h-3 w-3" /> Архив{t.available_until && ` · до ${new Date(t.available_until).toLocaleDateString("ru-RU")}`}
              </span>
            )}
          </div>
        </div>
        <span className="font-mono text-[11px] text-text-muted">#{t.id}</span>
      </div>

      <div className="px-4 pt-3">
        <p className="text-2xl font-bold tabular-nums text-text-primary">
          {money(t.price_value, t.currency)}
          <span className="ml-1 text-sm font-normal text-text-secondary">/ {days(t.duration_days || 30)}</span>
        </p>
        {t.price && t.price !== money(t.price_value, t.currency) && <p className="text-xs text-text-muted">в приложении: «{t.price}»</p>}
      </div>

      <ul className="flex-1 divide-y divide-border-primary px-4 py-2">
        <Perk label="VIP-часы" value={c.vip_hours || 0} off={!c.vip_hours} />
        <Perk label="Активных объявлений" value={maxAds} />
        <Perk label="Фото в объявлении" value={c.max_photos_per_ad ?? "—"} />
        <Perk label="Баннер на главной" value={c.banner_included ? "да" : "нет"} off={!c.banner_included} />
        <Perk label="Аналитика" value={c.analytics_enabled ? "да" : "нет"} off={!c.analytics_enabled} />
        <Perk label="Поддержка" value={SUPPORT[c.support_priority] || "обычная"} off={!c.support_priority || c.support_priority === "standard"} />
        {c.personal_manager && <Perk label="Персональный менеджер" value="да" />}
        {Number(c.search_boost_multiplier) > 1 && <Perk label="Буст в поиске" value={`×${c.search_boost_multiplier}`} />}
        {t.for_user_type === "business" && <Perk label="Комиссия с продаж" value={t.commission_rate === null || t.commission_rate === undefined ? "16% (по умолч.)" : `${t.commission_rate}%`} />}
      </ul>

      <div className="flex gap-2 border-t border-border-primary p-3">
        <button type="button" onClick={() => onEdit(t)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-tertiary">
          <PencilSquareIcon className="h-4 w-4" /> Изменить
        </button>
        <button
          type="button"
          onClick={() => onToggle(t)}
          title={t.is_active ? "Снять с продажи" : "Вернуть в продажу"}
          className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
        >
          {t.is_active ? "Скрыть" : "Показать"}
        </button>
        <button type="button" onClick={() => onDelete(t)} title="Удалить" aria-label="Удалить" className="rounded-lg border border-border-primary p-1.5 text-text-muted hover:border-rose-500/40 hover:text-rose-500">
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
};

function TariffsManagement() {
  const [tariffs, setTariffs] = useState([]);
  const [state, setState] = useState("loading");
  const [audience, setAudience] = useState("business");
  const [showHidden, setShowHidden] = useState(true);
  const [editor, setEditor] = useState(null); // { tariff | null }
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setState((s) => (s === "ready" ? "ready" : "loading"));
    try {
      setTariffs(await listTariffs());
      setState("ready");
    } catch (e) {
      console.error("Failed to load tariffs:", e);
      setState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Старые тарифы без for_user_type backend считает бизнесовыми
  const byAudience = useMemo(() => {
    const groups = { business: [], individual: [] };
    tariffs.forEach((t) => groups[t.for_user_type === "individual" ? "individual" : "business"].push(t));
    Object.values(groups).forEach((g) => g.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.price_value - b.price_value));
    return groups;
  }, [tariffs]);

  const visible = byAudience[audience].filter((t) => showHidden || t.is_active);
  const current = visible.filter((t) => !t.is_archived);
  const archived = visible.filter((t) => t.is_archived);

  const toggleActive = async (t) => {
    setBusyId(t.id);
    try {
      await updateTariff(t.id, { is_active: !t.is_active });
      setTariffs((list) => list.map((x) => (x.id === t.id ? { ...x, is_active: !t.is_active } : x)));
      setToast({ type: "success", text: t.is_active ? `«${t.name}» снят с продажи. Действующие подписки сохранятся.` : `«${t.name}» снова в продаже` });
    } catch (e) {
      setToast({ type: "error", text: e.status === 403 ? "Менять тарифы может только администратор." : e.message });
    } finally {
      setBusyId(null);
    }
  };

  const runDelete = async () => {
    setDeleting(true);
    setConfirmError(null);
    try {
      await deleteTariff(confirm.id);
      setToast({ type: "success", text: `Тариф «${confirm.name}» удалён` });
      setConfirm(null);
      load();
    } catch (e) {
      setConfirmError(
        e.status === 409
          ? `${e.message.match(/\d+/)?.[0] || "Есть"} пользователей с действующей подпиской на этот тариф — удалить нельзя. Снимите его с продажи кнопкой «Скрыть».`
          : e.status === 403
          ? "Удалять тарифы может только администратор."
          : e.message
      );
    } finally {
      setDeleting(false);
    }
  };

  const renderGrid = (list) => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {list.map((t) => (
        <TariffCard
          key={t.id}
          t={t}
          busy={busyId === t.id}
          onEdit={(x) => setEditor({ tariff: x })}
          onToggle={toggleActive}
          onDelete={(x) => {
            setConfirmError(null);
            setConfirm(x);
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Тарифы</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Подписки для продавцов: цена, срок и условия, которые применяет система</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
            <ArrowPathIcon className={`h-5 w-5 ${state === "loading" ? "animate-spin" : ""}`} />
          </button>
          <button type="button" onClick={() => setEditor({ tariff: null })} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
            <PlusIcon className="h-4 w-4" /> Новый тариф
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" className="flex rounded-lg border border-border-primary bg-bg-card p-1">
          {AUDIENCES.map((a) => (
            <button
              key={a.value}
              type="button"
              role="tab"
              aria-selected={audience === a.value}
              onClick={() => setAudience(a.value)}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium ${audience === a.value ? "bg-accent-grad text-white shadow-sm" : "text-text-secondary hover:text-text-primary"}`}
            >
              {a.label}
              <span className={`rounded-full px-1.5 text-xs tabular-nums ${audience === a.value ? "bg-white/20" : "bg-bg-tertiary text-text-muted"}`}>{byAudience[a.value].length}</span>
            </button>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
          Показывать скрытые
        </label>
      </div>

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {state === "error" ? (
        <div className="flex flex-col items-center gap-3 rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
          <p className="text-sm text-text-secondary">Не удалось загрузить тарифы</p>
          <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Повторить
          </button>
        </div>
      ) : state === "loading" && tariffs.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-80 animate-pulse rounded-block border border-border-primary bg-bg-card" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-14 text-center text-sm text-text-secondary">
          Тарифов для этой аудитории нет.
        </div>
      ) : (
        <div className="space-y-6">
          {current.length > 0 && renderGrid(current)}
          {archived.length > 0 && (
            <section>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
                <ArchiveBoxIcon className="h-4 w-4 text-amber-500" /> Архивная линейка
                <span className="font-normal text-text-muted">— продаётся до указанной даты, затем уходит из продажи</span>
              </h3>
              {renderGrid(archived)}
            </section>
          )}
        </div>
      )}

      {editor && (
        <TariffEditor
          tariff={editor.tariff}
          defaultUserType={audience}
          onClose={() => setEditor(null)}
          onSaved={(_, message) => {
            setEditor(null);
            setToast({ type: "success", text: message });
            load();
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={`Удалить тариф «${confirm.name}»?`}
          text="Тариф будет удалён безвозвратно. Если на него есть действующие подписки, удалить не получится — вместо этого снимите его с продажи."
          confirmLabel="Удалить"
          busy={deleting}
          error={confirmError}
          onConfirm={runDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default TariffsManagement;
